import { useState, useRef, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { getSession, signOut } from '../lib/auth.js';
import { getCurrentOrg, listAccessibleSubsidiaries } from '../lib/org-model.js';
import { getVertical } from '../data/mock/verticals.js';
import { getAccount } from '../lib/starlink-api.js';
import { startImpersonation } from '../lib/impersonation.js';
import { getRuntimeMode } from '../lib/runtime-mode.js';
import CommandPalette from './CommandPalette.jsx';
import { useHelpTour } from './HelpTour.jsx';
import { useUserMode } from '../lib/user-mode.jsx';

/**
 * Environment indicator pill — DEV / DEMO / PROD. Sits at the leading edge
 * of the breadcrumb row so anyone glancing at the header knows which
 * environment they're operating in. Critical for enterprise ops teams who
 * may have multiple environments side-by-side.
 */
function EnvBadge() {
  const mode = getRuntimeMode();
  // Customer-facing: real Cognito auth + real API = the live production
  // environment (PROD), even if Stripe payments aren't wired yet. Only the
  // mock-data build shows DEMO. Never show "DEV" to a real signed-in customer.
  let label = 'DEMO';
  let cls = 'bg-amber-50 border-amber-200 text-amber-800';
  if (mode.isProduction || (mode.realAuth && mode.realData)) {
    label = 'PROD'; cls = 'bg-emerald-50 border-emerald-200 text-emerald-800';
  } else if (mode.realAuth) {
    label = 'PROD'; cls = 'bg-emerald-50 border-emerald-200 text-emerald-800';
  }
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-bold tracking-[0.12em] uppercase ${cls}`}
      title={`Environment: ${label}`}
    >
      <span className="w-1 h-1 rounded-full bg-current opacity-60" />
      {label}
    </span>
  );
}

/**
 * TopBar — Inseego Connect inspired. Breadcrumbs on the left, global
 * search, alarms bell, account selector, user menu on the right.
 */
export default function TopBar({ onOpenMobileNav }) {
  const location = useLocation();
  const session = getSession();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const userMenuRef = useRef(null);

  const breadcrumbs = useBreadcrumbs(location.pathname);

  useEffect(() => {
    function handler(e) { if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ⌘K / Ctrl+K opens the command palette.
  // For Netcom staff (netcom-admin), the AdminCmdK component owns this
  // shortcut — it surfaces the god-mode jump targets (impersonate, audit
  // search, force-create, etc). Mounting both palettes on the same key
  // means a single press toggles both at once and they desync state.
  // Gate this one off for staff.
  const isStaff = !!session?.isNetcomAdmin;
  useEffect(() => {
    if (isStaff) return;
    function handler(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isStaff]);

  async function handleSignOut() {
    // signOut() is async (it tears down the Cognito session + clears
    // localStorage). Await it so the session is fully gone before we
    // leave. Then do a HARD redirect via window.location — not a
    // client-side navigate() — so the browser fetches a fresh
    // index.html (current chunk hashes) and rebuilds React state from
    // scratch. A soft navigate left stale in-memory state + risked a
    // blank page when the cached shell referenced deleted chunks.
    try { await signOut(); } catch { /* clear-and-go regardless */ }
    window.location.assign('/login');
  }

  return (
    <header className="h-14 bg-white border-b border-[var(--color-line-1)] sticky top-0 z-30 flex items-center justify-between px-5 safe-top">
      {/* Mobile hamburger */}
      <button
        onClick={onOpenMobileNav}
        aria-label="Open navigation"
        className="md:hidden p-2 -ml-2 text-[var(--color-ink-3)] hover:text-[var(--color-ink-1)] hover:bg-[var(--color-bg-hover)] rounded-md mr-1"
      >
        <svg className="w-5 h-5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h14M3 10h14M3 14h14" strokeLinecap="round" /></svg>
      </button>

      {/* Environment indicator + Breadcrumbs. The env pill is internal ops
          chrome — only Netcom staff see it; a customer shouldn't be shown a
          "PROD" badge (reads like a dev tool, not their portal). */}
      <nav className="flex-1 flex items-center gap-2 text-sm overflow-x-auto" aria-label="Breadcrumb">
        {session?.isNetcomAdmin && <EnvBadge />}
        {breadcrumbs.map((crumb, i) => (
          <span key={crumb.to} className="flex items-center gap-1">
            {i > 0 && <span className="text-[var(--color-ink-5)]">/</span>}
            {i === breadcrumbs.length - 1 ? (
              <span className="font-medium text-[var(--color-ink-1)]">{crumb.label}</span>
            ) : (
              <Link to={crumb.to} className="text-[var(--color-ink-3)] hover:text-[var(--color-brand-600)]">{crumb.label}</Link>
            )}
          </span>
        ))}
      </nav>

      {/* Right cluster */}
      <div className="flex items-center gap-1">
        {/* Search */}
        <button
          aria-label="Search"
          onClick={() => setPaletteOpen(true)}
          className="hidden md:flex items-center gap-2 px-2.5 py-1.5 text-[var(--color-ink-3)] hover:text-[var(--color-ink-1)] hover:bg-[var(--color-bg-hover)] rounded-md transition-colors border border-[var(--color-line-1)] text-xs"
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="9" r="6" />
            <path d="m14 14 4 4" strokeLinecap="round" />
          </svg>
          <span>Search</span>
          <kbd className="text-[10px] bg-[var(--color-bg-soft)] px-1 py-0.5 rounded border border-[var(--color-line-1)]">⌘K</kbd>
        </button>
        <button
          aria-label="Search"
          onClick={() => setPaletteOpen(true)}
          className="md:hidden p-2 text-[var(--color-ink-3)] hover:text-[var(--color-ink-1)] hover:bg-[var(--color-bg-hover)] rounded-md transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="9" cy="9" r="6" />
            <path d="m14 14 4 4" strokeLinecap="round" />
          </svg>
        </button>
        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

        {/* Help / Tour */}
        <HelpButton />

        {/* Alarms bell with dropdown */}
        <NotificationsBell />

        {/* Vertical divider */}
        <div className="w-px h-6 bg-[var(--color-line-1)] mx-2" />

        {/* Account switcher */}
        <AccountSwitcher session={session} />

        {/* User menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-2.5 pl-1.5 pr-2 py-1 rounded-md hover:bg-[var(--color-bg-hover)] transition-colors"
          >
            {/* Squared avatar — subtle gradient gives it depth without
                going decorative. Display-weight initials read as designed. */}
            <div
              className="w-8 h-8 rounded-md text-white flex items-center justify-center text-[12px] font-semibold display tracking-tight flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #1F2937 0%, #0A1A2E 100%)', boxShadow: '0 1px 2px rgba(10,14,26,0.10), 0 0 0 1px rgba(10,14,26,0.04)' }}
            >
              {userInitials(session?.name)}
            </div>
            <div className="hidden md:flex flex-col items-start leading-tight">
              <span className="display text-[13px] font-semibold text-[var(--color-ink-1)] tracking-tight">{session?.name}</span>
              <span className="text-[11px] text-[var(--color-ink-4)] font-medium">{session?.role || 'Owner'}</span>
            </div>
            <svg className="w-3 h-3 text-[var(--color-ink-4)]" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m3 4 3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {userMenuOpen && (
            <div className="absolute right-0 top-full mt-1 w-64 max-w-[calc(100vw-2rem)] bg-white border border-[var(--color-line-1)] rounded-md shadow-lg overflow-hidden">
              {/* Identity block — elegant sentence-case in display font */}
              <div className="px-4 py-3 border-b border-[var(--color-line-soft)] bg-[var(--color-bg-soft)]">
                <div className="text-[11px] text-[var(--color-ink-4)] font-medium">Signed in as</div>
                <div className="display font-semibold text-[var(--color-ink-1)] text-[15px] mt-0.5 truncate tracking-tight">{session?.name}</div>
                <div className="text-[12px] text-[var(--color-ink-3)] truncate">{session?.email}</div>
                <div className="text-[11px] text-[var(--color-ink-4)] mt-1.5 font-medium">
                  Role · <span className="text-[var(--color-ink-2)] font-semibold">{session?.role || 'Owner'}</span>
                </div>
              </div>
              <ModeSwitcher onPicked={() => setUserMenuOpen(false)} />
              <MenuItem to="/security" onClick={() => setUserMenuOpen(false)}>Security &amp; sessions</MenuItem>
              <MenuItem to="/settings/notifications" onClick={() => setUserMenuOpen(false)}>Notification settings</MenuItem>
              <MenuItem to="/audit" onClick={() => setUserMenuOpen(false)}>Audit log</MenuItem>
              <MenuItem to="/glossary" onClick={() => setUserMenuOpen(false)}>Glossary</MenuItem>
              <MenuItem to="/support" onClick={() => setUserMenuOpen(false)}>Help &amp; support</MenuItem>
              <button onClick={handleSignOut} className="w-full text-left px-3 py-2 text-[12px] tracking-normal font-semibold text-[var(--color-ink-2)] hover:bg-[var(--color-bg-hover)] hover:text-red-700 border-t border-[var(--color-line-soft)]">
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

/**
 * HelpButton — ? icon that triggers the contextual tour for the current page.
 */
function HelpButton() {
  const { start, replayAll, hasTour } = useHelpTour();
  const [menu, setMenu] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setMenu(false); }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setMenu(!menu)}
        aria-label="Help & tour"
        className="p-2 text-[var(--color-ink-3)] hover:text-[var(--color-ink-1)] hover:bg-[var(--color-bg-hover)] rounded-md transition-colors"
        title="Help & tour"
      >
        <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="10" cy="10" r="7.5" />
          <path d="M8 8a2 2 0 014 0c0 1-2 1.5-2 3M10 14h.01" strokeLinecap="round" />
        </svg>
      </button>
      {menu && (
        <div className="absolute right-0 top-full mt-1 w-60 max-w-[calc(100vw-2rem)] bg-white border border-[var(--color-line-1)] rounded-md shadow-lg overflow-hidden">
          <button
            disabled={!hasTour}
            onClick={() => { setMenu(false); start(); }}
            className="w-full text-left px-3 py-2 text-[12px] tracking-normal font-semibold text-[var(--color-ink-2)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-ink-1)] disabled:opacity-40 transition-colors"
          >
            Tour this page
          </button>
          <button onClick={() => { setMenu(false); replayAll(); }} className="w-full text-left px-3 py-2 text-[12px] tracking-normal font-semibold text-[var(--color-ink-2)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-ink-1)] border-t border-[var(--color-line-soft)] transition-colors">
            Replay all tours
          </button>
          <a href="/support" onClick={() => setMenu(false)} className="block px-3 py-2 text-[12px] tracking-normal font-semibold text-[var(--color-ink-2)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-ink-1)] border-t border-[var(--color-line-soft)] transition-colors">
            Contact support
          </a>
        </div>
      )}
    </div>
  );
}

/**
 * NotificationsBell — bell icon in TopBar with a dropdown of recent unacknowledged
 * alerts. Pulls from the same alerts feed; "See all" links into /alerts.
 */
function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const mod = await import('../lib/alerts.js');
      const data = await mod.listAlerts({ includeResolved: false });
      setAlerts(data.filter((a) => !a.acknowledged).slice(0, 6));
    })();
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        aria-label="Notifications"
        className="relative p-2 text-[var(--color-ink-3)] hover:text-[var(--color-ink-1)] hover:bg-[var(--color-bg-hover)] rounded-md transition-colors"
      >
        <svg className="w-4 h-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 8a4 4 0 018 0v3l1.5 2.5h-11L6 11V8zM8 16a2 2 0 004 0" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="absolute top-1 right-1 w-2 h-2 bg-[var(--color-danger)] rounded-full ring-2 ring-white" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-96 max-w-[calc(100vw-2rem)] bg-white border border-[var(--color-line-1)] rounded-lg shadow-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-[var(--color-line-soft)] flex items-center justify-between">
            <h3 className="text-sm font-semibold text-[var(--color-ink-1)]">Notifications</h3>
            <Link to="/alerts" className="text-xs font-medium text-[var(--color-brand-600)] hover:underline" onClick={() => setOpen(false)}>See all</Link>
          </div>
          <ul className="max-h-[400px] overflow-y-auto divide-y divide-[var(--color-line-soft)]">
            {alerts.length === 0 && (
              <li className="px-4 py-6 text-sm text-[var(--color-ink-4)] text-center">You're all caught up — nothing needs your attention.</li>
            )}
            {alerts.map((a) => (
              <li key={a.id} className="px-4 py-3 hover:bg-[var(--color-bg-hover)]">
                <div className="flex items-start gap-2.5">
                  <SeverityDot severity={a.severity} />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[var(--color-ink-1)] text-sm">{a.title}</div>
                    <div className="text-[11px] text-[var(--color-ink-4)] mt-0.5">{a.deviceLabel}</div>
                    <div className="text-[10px] text-[var(--color-ink-5)] mt-1">{timeAgo(a.triggeredAt)}</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SeverityDot({ severity }) {
  const colors = { critical: 'bg-red-500', warning: 'bg-amber-500', info: 'bg-cyan-500', resolved: 'bg-emerald-500' };
  return <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${colors[severity] || 'bg-slate-400'}`} />;
}

function timeAgo(iso) {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
}

/**
 * AccountSwitcher — for customers with multiple Starlink accounts under
 * one Netcom relationship. Currently shows only the active account name,
 * but the dropdown UI is in place for when we have real multi-account state.
 */
function AccountSwitcher({ session }) {
  const [open, setOpen] = useState(false);
  const [account, setAccount] = useState(null);
  const ref = useRef(null);

  // Pull the real account record so the switcher shows the BUSINESS name
  // (e.g. "RADIUS SECURITY") — the same source the Dashboard header uses —
  // instead of a possessive guess from the signed-in person's name. Live mode
  // hits /accounts; mock mode resolves from the bundled data. Fails soft.
  useEffect(() => {
    let alive = true;
    const acct = session?.accountNumber;
    if (!acct || acct === 'REPLACE_ME') { setAccount(null); return; }
    getAccount(acct).then((a) => { if (alive) setAccount(a); }).catch(() => {});
    return () => { alive = false; };
  }, [session?.accountNumber]);

  // Map raw account numbers to friendly display names. Order of preference:
  // demo verticals → known demo orgs → the real account's business/org name →
  // the impersonation businessName → finally a "<lastname>'s account" guess.
  function friendlyName(accountNumber, sessionName, businessName) {
    if (!accountNumber || accountNumber === 'REPLACE_ME') return 'Demo fleet';
    const v = getVertical(accountNumber);
    if (v) return v.account.accountName;
    if (accountNumber.includes('DEMO-AUDIT')) return 'Globaltrade Energy';
    if (businessName) return businessName;
    if (sessionName) return `${sessionName.split(' ').slice(-1)[0]}'s account`;
    return 'Your account';
  }
  // Same order the Dashboard header uses, so the switcher shows the identical
  // org string ("RADIUS SECURITY") rather than a different variant.
  const businessName =
    account?.accountName || account?.businessName || session?.businessName || null;
  const displayName = friendlyName(session?.accountNumber, session?.name, businessName);
  // Org rollup — if the current account belongs to a holding company,
  // surface the parent + sibling subsidiaries in the switcher. Lets a
  // parent-level admin pivot between BUs without re-auth.
  const org = getCurrentOrg(session?.accountNumber);
  const subsidiaries = org ? listAccessibleSubsidiaries(org.orgId) : [];
  const accounts = subsidiaries.length
    ? subsidiaries.map((s) => ({
        accountNumber: s.accountNumber,
        name: s.name,
        role: s.accountNumber === session?.accountNumber ? 'You' : 'Switch',
        bu: s.bu,
      }))
    : [
        { accountNumber: session?.accountNumber, name: displayName, role: 'Owner' },
      ];

  useEffect(() => {
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative hidden md:block" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex flex-col items-end pr-1 hover:opacity-80 transition-opacity"
        title="Switch account"
      >
        <span className="text-[11px] text-[var(--color-ink-4)] font-medium">Account</span>
        <span className="display text-[14px] text-[var(--color-ink-1)] font-semibold tracking-tight flex items-center gap-1">
          {displayName}
          <svg className="w-2.5 h-2.5 text-[var(--color-ink-4)]" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="m2 4 3 3 3-3" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-96 bg-white border border-[var(--color-line-1)] rounded-md shadow-lg overflow-hidden">
          {/* Org header — only renders when the user is on a holding-co
              account. Shows the parent org name + Globaltrade-style
              rollup ("3 subsidiaries · 50 sites"). Click → org rollup
              view (not built tonight; placeholder). */}
          {org && (
            <div className="px-4 py-3 bg-[var(--color-ink-1)] text-white">
              <div className="text-[11px] tracking-normal text-white/55 font-semibold">Holding company</div>
              <div className="display text-[15px] font-semibold tracking-tight mt-0.5 truncate">{org.name}</div>
              <div className="text-[11px] text-white/55 mt-0.5">
                {org.subsidiaries.length} subsidiaries · {org.subsidiaries.reduce((s, x) => s + x.siteCount, 0)} sites total
              </div>
            </div>
          )}
          <div className="px-4 py-2.5 border-b border-[var(--color-line-soft)] text-[11px] tracking-normal text-[var(--color-ink-4)] font-semibold bg-[var(--color-bg-soft)]">
            {org ? 'Subsidiaries you can act as' : 'Your accounts'}
          </div>
          {accounts.map((a) => {
            const isYou = a.accountNumber === session?.accountNumber;
            return (
              <div key={a.accountNumber} className={`px-4 py-3 flex items-center justify-between gap-3 ${isYou ? 'bg-emerald-50/40' : 'hover:bg-[var(--color-bg-hover)]'}`}>
                <div className="min-w-0">
                  <div className="display font-semibold text-[var(--color-ink-1)] text-[14px] tracking-tight truncate">{a.name}</div>
                  <div className="text-[11px] text-[var(--color-ink-4)] truncate">
                    <span className="mono">{a.accountNumber}</span>
                    {a.bu && <> · {a.bu}</>}
                  </div>
                </div>
                {isYou ? (
                  <span className="text-[11px] tracking-normal font-semibold text-emerald-700 flex-shrink-0">Current</span>
                ) : (
                  <button
                    onClick={() => {
                      // Switch the active account context to this subsidiary.
                      // Uses the impersonation channel (the signed-in user is
                      // an org-parent / netcom-admin who is authorized to act
                      // as any subsidiary under the holding company). Every
                      // action is still audit-logged with X-Assume-Account.
                      // Hard-navigate to "/" so all data re-resolves for the
                      // newly-active subsidiary account.
                      startImpersonation({
                        accountNumber: a.accountNumber,
                        businessName: a.name,
                        email: session?.email,
                        actorEmail: session?.email,
                      });
                      window.location.assign('/');
                    }}
                    className="text-[11px] tracking-normal font-semibold text-[var(--color-ink-3)] hover:text-white hover:bg-[var(--color-brand-600)] border border-[var(--color-line-1)] hover:border-[var(--color-brand-600)] px-2 py-1 rounded flex-shrink-0 transition-colors"
                    title={`Act as ${a.name}`}
                    aria-label={`Switch to ${a.name}`}
                  >
                    Switch →
                  </button>
                )}
              </div>
            );
          })}
          <div className="border-t border-[var(--color-line-soft)] px-4 py-2.5 text-[11px] text-[var(--color-ink-4)] leading-snug">
            {org ? (
              <>Org-level rollup view <span className="text-[var(--color-ink-3)]">(coming soon)</span> · or <a href="/support" className="text-[var(--color-brand-600)] font-semibold hover:underline">contact your Netcom team</a>.</>
            ) : (
              <>Need access to another account? <a href="/support" className="text-[var(--color-brand-600)] font-semibold hover:underline">Contact your Netcom team</a>.</>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * ModeSwitcher — Simple ↔ Pro toggle inside the user menu.
 */
function ModeSwitcher({ onPicked }) {
  const { setMode, isSimple } = useUserMode();
  return (
    <div className="px-3 py-2.5 border-b border-[var(--color-line-soft)]">
      <div className="text-[11px] tracking-normal text-[var(--color-ink-4)] font-semibold mb-1.5">View mode</div>
      <div className="flex items-center gap-1 bg-[var(--color-bg-soft)] border border-[var(--color-line-1)] rounded-md p-0.5 text-xs">
        <button
          onClick={() => { setMode('simple'); onPicked?.(); }}
          className={`flex-1 px-2.5 py-1 rounded font-medium transition-colors ${isSimple ? 'bg-white text-[var(--color-ink-1)] shadow-sm' : 'text-[var(--color-ink-4)] hover:text-[var(--color-ink-2)]'}`}
        >
          Simple
        </button>
        <button
          onClick={() => { setMode('pro'); onPicked?.(); }}
          className={`flex-1 px-2.5 py-1 rounded font-medium transition-colors ${!isSimple ? 'bg-white text-[var(--color-ink-1)] shadow-sm' : 'text-[var(--color-ink-4)] hover:text-[var(--color-ink-2)]'}`}
        >
          Pro
        </button>
      </div>
      <div className="text-[10px] text-[var(--color-ink-4)] mt-1.5">
        {isSimple ? 'Streamlined for day-to-day use.' : 'Full enterprise toolkit.'}
      </div>
    </div>
  );
}

/**
 * MenuItem — mono uppercase ops-console row for the user dropdown. No emoji,
 * no chrome — just a tight link that matches the rest of the enterprise pass.
 */
function MenuItem({ to, onClick, children }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="block px-4 py-2 text-[13px] font-medium text-[var(--color-ink-2)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-ink-1)] transition-colors"
    >
      {children}
    </Link>
  );
}

/**
 * userInitials — first letter of first + last token, mono-friendly. Falls
 * back to 'U' if no name. Keeps the avatar tile rendering consistent even
 * if the session payload is missing display name.
 */
function userInitials(name) {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function useBreadcrumbs(pathname) {
  const segments = pathname.split('/').filter(Boolean);
  const crumbs = [{ to: '/', label: 'Home' }];
  // Title Case explicit labels for routes whose URL segment doesn't naturally
  // capitalize to a polished display value. Anything not listed here falls
  // through to the smart fallback below (hyphen → space + Title Case).
  const labels = {
    'terminals': 'Terminals',
    'network': 'Network Map',
    'service-lines': 'Service Lines',
    'groups': 'Groups & Tags',
    'alerts': 'Alerts',
    'sla': 'SLA Performance',
    'trends': 'Trends & Reports',
    'audit': 'Audit Log',
    'firmware': 'Firmware',
    'thresholds': 'Alert Rules',
    'team': 'Team',
    'reports': 'Reports',
    'sites': 'Sites',
    'speed-test': 'Speed Test',
    'api-access': 'API Access',
    'optimizer': 'Cost Optimizer',
    'billing': 'Billing',
    'requests': 'Service Requests',
    'new': 'New Request',
    'support': 'Help & Support',
    'security': 'Security',
    'sims': 'SIM Cards',
    'dashboards': 'Custom Dashboards',
    'add-kit': 'Add Existing Dish',
    'glossary': 'Glossary',
    'pay': 'Pay Invoice',
    'welcome': 'Welcome',
    'settings': 'Settings',
    'sso': 'SSO',
    'scim': 'SCIM',
    'notifications': 'Notifications',
    'trust': 'Trust Center',
    'sub-processors': 'Sub-processors',
    'data-residency': 'Data Residency',
    'certifications': 'Certifications',
  };
  // Smart fallback: split-on-hyphen + Title Case each word. Handles arbitrary
  // path segments (e.g., a future "real-time-telemetry" route shows as
  // "Real Time Telemetry" without explicit registration).
  function smartLabel(seg) {
    return seg
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  let path = '';
  for (const seg of segments) {
    path += '/' + seg;
    crumbs.push({ to: path, label: labels[seg] || smartLabel(seg) });
  }
  return crumbs;
}
